// WantlistExport: Pure utilities + modal component für den Wantlist-Export.
// Unterstützt Text, Cardmarket (mit Set-Namen bei explizit gewähltem Print) und CSV.
// Modal zeigt Live-Preview, "In Zwischenablage" und "Datei herunterladen".

import { useState, useMemo } from 'react';
import ExportModal from '../ui/ExportModal';
import useCardSets from '../hooks/useCardSets';
import useCardmarketRefs from '../hooks/useCardmarketRefs';

export const WANTLIST_EXPORT_FORMATS = [
  { value: 'text', label: 'Text' },
  { value: 'cardmarket', label: 'Cardmarket' },
  { value: 'csv', label: 'CSV' },
];

// --- Pure utilities ---

/**
 * Generate export content for all checked cards in given format.
 * @param {Array} cards — enriched cards (with isChecked, missing, name, setCode, printLocked, scryfallId)
 * @param {'text'|'cardmarket'|'csv'} format
 * @param {Object} [setMap={}] — `{ [setCode]: { name, cardmarketName } }`, wie beim Deck-Export
 * @param {Object} [cardmarketRefs={}] — `{ [scryfallId]: { expansion, version } | null }`, greift NUR im
 *   Cardmarket-Format und NUR bei `card.printLocked`; hat Vorrang vor `setMap`.
 * @returns {string}
 */
export function generateWantlistExport(cards, format = 'text', setMap = {}, cardmarketRefs = {}) {
  const exportCards = (cards || []).filter((c) => c.isChecked && c.missing > 0);

  switch (format) {
    case 'text':
      // Simple flat list: "1 Sol Ring"
      return exportCards.map((c) => `${c.missing} ${c.name}`).join('\n');
    case 'cardmarket':
      // Flat list + (Cardmarket-Set-Name) bei explizit gewähltem Print;
      // bei vorhandenem cardmarketRefs-Eintrag (Ref über scryfallId) geht die
      // versions-genaue Expansion (` (V.N) (Expansion)`) vor dem setMap-Fallback.
      return exportCards
        .map((c) => {
          if (!c.printLocked) return `${c.missing} ${c.name}`;
          const ref = cardmarketRefs[c.scryfallId];
          if (ref) {
            const vPart = ref.version != null ? ` (V.${ref.version})` : '';
            return `${c.missing} ${c.name}${vPart} (${ref.expansion})`;
          }
          const cmName = c.setCode ? setMap[c.setCode]?.cardmarketName : null;
          const setPart = cmName ? ` (${cmName})` : '';
          return `${c.missing} ${c.name}${setPart}`;
        })
        .join('\n');
    case 'csv': {
      const header = 'Quantity,Name,Set,Price';
      const rows = exportCards.map(
        (c) =>
          `${c.missing},"${(c.name || '').replace(/"/g, '""')}",${
            c.setCode || ''
          },${c.price ?? ''}`,
      );
      return [header, ...rows].join('\n');
    }
    default:
      return '';
  }
}

// --- Modal component ---

export default function WantlistExportModal({ cards, isOpen, onClose, title = 'Export' }) {
  const [format, setFormat] = useState('text');
  const { data: setData } = useCardSets(isOpen);
  const setMap = useMemo(() => setData?.sets || {}, [setData]);

  const exportCards = useMemo(
    () => (cards || []).filter((c) => c.isChecked && c.missing > 0),
    [cards],
  );
  const cardCount = exportCards.length;
  const totalCount = exportCards.reduce((sum, c) => sum + c.missing, 0);

  const lockedIds = useMemo(
    () => [...new Set(exportCards.filter(c => c.printLocked && c.scryfallId).map(c => c.scryfallId))],
    [exportCards]
  );
  const { data: refData } = useCardmarketRefs(lockedIds, isOpen);
  const cardmarketRefs = useMemo(() => refData?.refs || {}, [refData]);

  const content = useMemo(
    () => generateWantlistExport(cards, format, setMap, cardmarketRefs),
    [cards, format, setMap, cardmarketRefs],
  );

  return (
    <ExportModal
      open={isOpen}
      onOpenChange={(open) => { if (!open) onClose?.(); }}
      title={title}
      formats={WANTLIST_EXPORT_FORMATS}
      format={format}
      onFormatChange={setFormat}
      stats={`${cardCount} Karten · ${totalCount} Exemplare`}
      content={content}
      filename={(f) => `wantlist.${f === 'csv' ? 'csv' : 'txt'}`}
      empty={cardCount === 0}
    />
  );
}
